/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2018 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

import { isMarkupAnnot } from "utils/QuickToolsUtils";
import { getPageHandle, getAccessiblePageContentHandle } from "selectors/pageSelectors";
import { getTabbablePDFElementsInPage } from "selectors/accessibilitySelectors";
import { pageContentHandler } from "pageContentHandler";
import { a11yClasses } from "constants/enums";

export const isListItemNode = node => node.contentRole === "listitem";
export const isListNode = node => node.contentRole === "list";
export const isFormNode = node => node.contentRole === "form";
export const isMarkupAnnotNode = node =>
  node.contentRole === "annotation" &&
  isMarkupAnnot(node.info && node.info.annotationType && node.info.annotationType.toLowerCase());
export const isLinkNode = node => node.contentRole === "link";
export const isContainerNode = node =>
  ["root", "paragraph", "list", "listitem", "table", "row", "cell"].includes(node.contentRole);
export const getFocusableDescendants = element => {
  const focusableElements =
    'a:not([disabled]), button:not([disabled]), input[type=text]:not([disabled]), [tabindex]:not([disabled]):not([tabindex="-1"])';
  return element.querySelectorAll(focusableElements);
};
export const getIsFocusable = node => isMarkupAnnotNode(node) || isLinkNode(node) || isFormNode(node);
export const getIsFocusableAccessibleDOMNode = node =>
  node.classList.contains(a11yClasses.A11Y_COMMENT) || node.classList.contains(a11yClasses.A11Y_LINK);

export const findAndFocusTabbableItemOutsideDOM = (stores, pageIndex, isFirst) => {
  const tabbableElementsInPage = getTabbablePDFElementsInPage(stores, pageIndex);
  if (tabbableElementsInPage && tabbableElementsInPage.length > 0) {
    const element = tabbableElementsInPage[isFirst ? 0 : tabbableElementsInPage.length - 1];
    stores.accessibilityStore.scrollToFocusElementWithTab(pageIndex, element);
    return true;
  }
  return false;
};

export const findAndFocusTabbableItemInsideDOM = (stores, pageIndex, isFirst) => {
  const pageHandle = getPageHandle(stores, pageIndex);
  const tabbableContentInPage = getFocusableDescendants(pageHandle);
  if (tabbableContentInPage && tabbableContentInPage.length) {
    tabbableContentInPage[isFirst ? 0 : tabbableContentInPage.length - 1].focus();
    return true;
  }
  return false;
};

export const simulateClick = (evt, pageIndex, stores) => {
  const { width, left, height, top } = evt.target.getBoundingClientRect();

  const elementHandle = getAccessiblePageContentHandle(stores, pageIndex);
  let eventObject = {
    target: elementHandle,
    pageX: left + width / 2,
    pageY: top + height / 2
  };

  /**
   * We need to simulate a click event here so that pageContentHandler
   * can do it's work. Since pageContentHandler depends on the order
   * of events (requires mouseDown to have been triggered before click)
   * we need to simulate all of them
   */

  pageContentHandler.handleMouseDown(eventObject, pageIndex, stores);

  pageContentHandler.handleMouseUp(eventObject, pageIndex, stores);

  pageContentHandler.handleMouseMove(eventObject, pageIndex, stores).then(() => {
    pageContentHandler.handleClick(eventObject, pageIndex, stores);
  });
};

export const getHTMLFontFamily = fontFamily => {
  // remove embedded subset font info. if present
  // for embedded subset fonts, a tag in Capital letters is also present
  // in font family name with '+' operaotr, e.g. "Trebuchet MS+FPEF"
  // as HTML doesn't understand it, so removing this data from fontFamilyName
  const embededFontInfoIdx = fontFamily.indexOf("+");
  return embededFontInfoIdx !== -1 ? fontFamily.substring(0, embededFontInfoIdx) : fontFamily;
};
